import React, { useEffect, useMemo, useState } from 'react';
import { matrizActual, guardarMatriz, restaurarMatriz, severidad, opcionesPlan } from '../engine/matriz';
import { fmtPesos, productoMvp, nombreProducto } from '../engine/perfil';

// S2 · #matriz (modo presentación): la aseguradora parametriza su apetito de riesgo y ve el efecto en vivo.
const PREG = { dependen: 'Quién depende', diaadia: 'Día a día', ahorro: 'Colchón de ahorro', edad: 'Rango de edad' };
const MULTI = { dependen: true, diaadia: true };
const PRODS = ['capital-vida', 'cancer-integral', 'vida-medida'];

export default function Matriz() {
  const [m, setM] = useState(matrizActual);
  const [perfil, setPerfil] = useState({ dependen: 'Mis hijos', diaadia: 'Trabajo desde casa', ahorro: 'De 6 a 12 meses', edad: '36–45' });
  useEffect(() => {
    const fn = () => setM(matrizActual());
    window.addEventListener('mbm-matriz', fn);
    return () => window.removeEventListener('mbm-matriz', fn);
  }, []);
  const cambiar = (fn) => { const n = JSON.parse(JSON.stringify(m)); fn(n); setM(n); guardarMatriz(n); };
  const sev = useMemo(() => severidad(perfil, m), [perfil, m]);
  const toggle = (preg, resp) => setPerfil((p) => {
    const sel = String(p[preg] || '').split(',').map((s) => s.trim()).filter(Boolean);
    let n;
    if (!MULTI[preg]) n = [resp];
    else if (sel.includes(resp)) n = sel.filter((x) => x !== resp);
    else n = preg === 'dependen' && resp === 'Solo yo' ? [resp] : [...sel.filter((x) => x !== 'Solo yo'), resp];
    return { ...p, [preg]: n.join(', ') };
  });
  const marcado = (preg, resp) => String(perfil[preg] || '').split(',').map((s) => s.trim()).includes(resp);

  return (
    <div className="mz" data-testid="pagina-matriz">
      <header className="mz-head">
        <a href="#" className="mz-volver">← Volver</a>
        <h1>Matriz de riesgo</h1>
        <p>A mayor riesgo, menos valor asegurado. Cada aseguradora define su propio apetito. {m.rotulo}</p>
      </header>

      <div className="mz-grid">
        <section className="mz-card">
          <h2>1 · Puntos por respuesta</h2>
          {Object.entries(m.puntos).map(([preg, tabla]) => (
            <div key={preg} className="mz-preg">
              <span className="mz-k">{PREG[preg] || preg}</span>
              {Object.entries(tabla).map(([resp, pts]) => (
                <label key={resp} className="mz-fila">
                  <span>{resp}</span>
                  <input type="number" min="0" max="9" value={pts} onChange={(e) => cambiar((n) => { n.puntos[preg][resp] = Number(e.target.value) || 0; })} data-testid={`mz-pts-${preg}-${resp}`} />
                </label>
              ))}
            </div>
          ))}
        </section>

        <section className="mz-card">
          <h2>2 · Severidad</h2>
          <label className="mz-fila"><span>Media desde (puntos)</span><input type="number" value={m.umbrales.media} onChange={(e) => cambiar((n) => { n.umbrales.media = Number(e.target.value) || 0; })} data-testid="mz-umbral-media" /></label>
          <label className="mz-fila"><span>Alta desde (puntos)</span><input type="number" value={m.umbrales.alta} onChange={(e) => cambiar((n) => { n.umbrales.alta = Number(e.target.value) || 0; })} data-testid="mz-umbral-alta" /></label>

          <h2>3 · Apetito por producto</h2>
          {PRODS.map((id) => {
            const c = m.productos[id];
            const planes = c.mecanismo === 'planes';
            return (
              <div key={id} className="mz-preg">
                <span className="mz-k">{c.aseguradora}</span>
                <span className="mz-nota">{planes ? 'Cuántos planes puede ver (de menor a mayor)' : 'Tope del valor asegurado (fracción del máximo de la ficha)'}</span>
                {['media', 'alta'].map((nv) => (
                  <label key={nv} className="mz-fila">
                    <span>{`Severidad ${nv}`}</span>
                    <input type="number" step={planes ? 1 : 0.05} min={planes ? 1 : 0.1} max={planes ? 5 : 1} value={c[nv]} onChange={(e) => cambiar((n) => { n.productos[id][nv] = Number(e.target.value); })} data-testid={`mz-${id}-${nv}`} />
                  </label>
                ))}
              </div>
            );
          })}
          <button type="button" className="mz-reset" onClick={() => { restaurarMatriz(); }} data-testid="mz-restaurar">Restaurar valores propuestos</button>
        </section>

        <section className="mz-card mz-prueba">
          <h2>Pruébalo con un perfil</h2>
          {Object.keys(PREG).map((preg) => (
            <div key={preg} className="mz-preg">
              <span className="mz-k">{PREG[preg]}</span>
              <div className="pf-opts multi">
                {Object.keys(m.puntos[preg]).map((resp) => (
                  <button key={resp} type="button" className={marcado(preg, resp) ? 'on' : ''} onClick={() => toggle(preg, resp)}>{resp}</button>
                ))}
              </div>
            </div>
          ))}
          <div className={`mz-sev ${sev.nivel}`} data-testid="mz-severidad">
            {`Severidad ${sev.etiqueta} · ${sev.total} puntos`}
            <small>{sev.detalle.map(([r, p]) => `${r} +${p}`).join(' · ') || 'Sin factores de riesgo'}</small>
          </div>
          <span className="mz-k">Lo que el cliente puede comprar</span>
          {PRODS.map((id) => {
            const op = opcionesPlan(id, perfil, m);
            return (
              <div key={id} className="mz-res" data-testid={`mz-res-${id}`}>
                <b>{nombreProducto(productoMvp(id))}</b>
                {op.modo === 'caja'
                  ? <span>{`De ${fmtPesos(op.min)} a ${fmtPesos(op.max)}`}{op.limitado ? ` (la ficha permite ${fmtPesos(op.maxFicha)})` : ''}</span>
                  : <span>{op.planes.map((p) => `${p.label} · ${fmtPesos(p.prima)}/mes`).join('  ·  ') || 'Según rango de edad'}{op.ocultos ? ` (${op.ocultos} oculto${op.ocultos > 1 ? 's' : ''})` : ''}</span>}
              </div>
            );
          })}
        </section>
      </div>
    </div>
  );
}
